/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Card,
  Row,
  Input,
  message,
} from 'antd';
import { useSelector } from 'react-redux';
import { onError } from 'utils/handlers';
import Select from 'components/Select';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import PlainTransfer from 'components/PlainTransfer';
import { getTiposDeDato } from 'api/configuracion';
import { getPeriodosFiscales } from 'api/catalogos/periodosFiscales';
import { getTiposDeCargosCartera } from 'api/recaudacion/tiposDeCargo';
import { getContenTypePorTipoDePadron } from 'api/catalogos';
import { padronesEnum } from 'views/Tramites/GPM/enums';

import {
  getActCarteras,
  deleteActCartera,
  patchActCartera,
  postActCartera,
  postCargos,
  getCargos,
  baseURI,
} from 'api/configuracion/actualizadorCarteras';
import { getPadrones } from 'utils/padrones';

import { permissionPropTypes } from 'utils/permission/propTypes';

export const permissionCarteras = {
  model: 'configuraciondeactualizadordecarteras',
  title: 'carterasConfig',
  catalogs: [
    'periodofiscal',
    'tipodecargo',
    'tipodedato',
    'padrones',
  ],
};

const ActualizadorCarteras = ({
  callback,
  disabled,
  permission,
  setCurrentTabKey,
  setForm,
}) => {
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [periodosFiscales, setPeriodosFiscales] = useState([]);

  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [tiposDeCargos, setTiposDeCargos] = useState([]);
  const [padrones, setPadrones] = useState([]);
  const [contentTypePadron, setContentTypePadron] = useState(null);
  const isEditing = selectedRowKeys.length > 0;
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [cargosSeleccionados, setCargosSeleccionados] = useState([]);

  const formValuesRef = useRef({});
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);

  const fetchData = async () => {
    setLoading(true);
    const _carteras = await getActCarteras();
    const carterasFiltradas = _carteras?.filter((c) => c.estados_globales !== 5);
    setData(carterasFiltradas);
    setLoading(false);
  };
  const fetchAll = async () => {
    const [
      _periodosFiscales,
      _padrones,
    ] = await Promise.all([
      getPeriodosFiscales(),
      getPadrones(),
    ]);
    setPeriodosFiscales(_periodosFiscales);
    setPadrones(_padrones);
  };
  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      await getTiposDeDato();
      const _carteras = await getActCarteras();
      const carterasFiltradas = _carteras?.filter((c) => c.estados_globales !== 5);
      fetchAll();
      if (mounted) {
        setData(carterasFiltradas);
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
    setSelectedRecord(null);
    setTiposDeCargos([]);
    setCargosSeleccionados([]);
  };
  useEffect(() => {
    if (!tiposDeCargos.length) return;
    if (!cargosSeleccionados.length) return;

    form.setFieldsValue({
      tipos_de_cargos: cargosSeleccionados,
    });
  }, [tiposDeCargos, cargosSeleccionados]);

  const onFinish = async () => {
    let carteraId = null;
    try {
      setLoading(true);
      await form.validateFields();

      const values = form.getFieldsValue();
      values.periodo_fiscal = isEditing
        ? values.periodo_fiscal
        : periodoFiscalSelected?.id;
      const [key] = selectedRowKeys;
      const { tipos_de_cargos, ...carteraValues } = values;

      carteraValues.content_type_padron = carteraValues.content_type_padron
      ?? contentTypePadron;

      carteraValues.estados_globales = carteraValues.estados_globales ?? 1;

      const response = await (
        key
          ? patchActCartera(key, carteraValues)
          : postActCartera(carteraValues)
      );
      if (response) {
        carteraId = response.id;

        const ids = tipos_de_cargos.map((e) => e.id ?? e);

        await postCargos({
          configuracion_de_actualizador_id: carteraId,
          tipos_de_cargos_ids: ids,
        });

        onCancel();
        callback(response);
        await fetchData();
        message.success('Datos guardados exitosamente');
      }
    } catch (err) {
      if (!isEditing && carteraId) {
        await deleteActCartera(carteraId);
      }
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const handlePadronChange = async (padronId, periodoFiscalId = null, fromEdit = false) => {
    try {
      setLoading(true);
      const isEditFlow = fromEdit;
      if (!isEditFlow) {
        form.setFieldsValue({
          tipos_de_cargos: [],
        });
      }

      const contentType = getContenTypePorTipoDePadron(padronId);
      setContentTypePadron(contentType);

      const cargosResponse = await getTiposDeCargosCartera(
        { padron_id: padronId, periodo_fiscal_id: periodoFiscalId },
        !isEditFlow,
      );

      const cargosArray = Object.values(cargosResponse).filter(
        (c) => c && c.id,
      );

      setTiposDeCargos(cargosArray);

      form.setFieldsValue({
        content_type_padron: contentType,
        periodo_fiscal: isEditFlow
          ? periodoFiscalId
          : periodoFiscalSelected?.id,
      });
    } catch (err) {
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOnRowClick = (record) => {
    if (disabled) return;

    setSelectedRowKeys([record.id]);
    setSelectedRecord(record);
  };

  const onClickAdd = () => {
    onCancel();
    form.setFieldsValue({
      periodo_fiscal: periodoFiscalSelected?.id,
    });
    setCargosSeleccionados([]);
    setVisible(true);
  };
  useEffect(() => {
    if (!visible) return;
    if (!periodosFiscales.length) return;
    if (isEditing) return;

    const padronId = form.getFieldValue('tipo_de_padron');

    if (padronId) {
      handlePadronChange(padronId, periodoFiscalSelected?.id);
    } else if (!isEditing) {
      form.setFieldsValue({
        periodo_fiscal: periodoFiscalSelected?.id,
      });
    }
  }, [visible, periodoFiscalSelected, periodosFiscales, isEditing]);

  const onClickDelete = () => setVisibleAlert(true);
  const onClickEdit = async () => {
    if (!selectedRecord) return;

    setVisible(true);

    form.setFieldsValue({
      ...selectedRecord,
    });

    await handlePadronChange(
      selectedRecord.tipo_de_padron,
      selectedRecord.periodo_fiscal,
      true,
    );

    const cargosResp = await getCargos({
      configuracion_de_actualizador: selectedRecord.id,
    });
    setCargosSeleccionados(
      cargosResp.map((c) => c.tipodecargo.id ?? c.tipodecargo),
    );

    form.setFieldsValue({
      tipos_de_cargos: cargosResp.map((c) => c.tipodecargo),
    });
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteActCartera(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };
  useEffect(() => {
    if (disabled) {
      setSelectedRowKeys([]);
      setVisible(false);
    }
  }, [disabled]);
  const onFinishHandler = async (tabKey = null, _continue = true) => {
    if (!visible && !tabKey) {
      message.info('Sin cambios realizados en Actualizador de Carteras');
      if (callback) {
        callback(null, false);
      }
      return null;
    }
    try {
      if (tabKey) {
        if (setCurrentTabKey) {
          setCurrentTabKey(tabKey);
        }
      } else {
        await onFinish(_continue);
      }
    } catch (err) {
      onError(err, setLoading);
    }
    return null;
  };
  useEffect(() => {
    if (setForm) {
      const formInstance = {
        getFieldValue: (name) => {
          if (form && form.getFieldValue) {
            return form.getFieldValue(name);
          }
          return formValuesRef.current[name];
        },
        setFieldsValue: (values) => {
          if (form && form.setFieldsValue) {
            form.setFieldsValue(values);
          }
          formValuesRef.current = { ...formValuesRef.current, ...values };
        },
        validateFields: async () => {
          if (visible && form && form.validateFields) {
            return form.validateFields();
          }
          return formValuesRef.current;
        },
        resetFields: () => {
          if (form && form.resetFields) {
            form.resetFields();
          }
          formValuesRef.current = {};
        },
        submit: () => onFinishHandler(null, true),
        onFinishHandler: (tabKey = null, _continue = true) => onFinishHandler(tabKey, _continue),
      };

      setForm(formInstance);
    }
  }, [setForm, form, visible]);

  const rowSelection = disabled ? null : {
    selectedRowKeys,
    type: 'radio',
    onSelect: () => {
      if (disabled) {
        return false;
      }
      return true;
    },
    getCheckboxProps: () => ({
      disabled,
    }),
  };
  const render = (e) => `${e.periodo} - ${e.identificador}${e.vigente ? ' (Vigente)' : ''}`;
  const columns = [
    {
      titleText: 'ID',
      key: 'id',
      dataIndex: 'id',
    },
    {
      titleText: 'Descripción',
      key: 'descripcion',
      dataIndex: 'descripcion',
    },
    {
      titleText: 'Tipo de  padron',
      key: 'tipo_de_padron',
      dataIndex: 'tipo_de_padron',
      render: (value) => Object.keys(padronesEnum).find((key) => padronesEnum[key]
      === value) || value,
    },
    {
      titleText: 'Periodo Fiscal',
      key: 'periodo_fiscal',
      dataIndex: 'periodo_fiscal',
      render: (periodoId) => {
        const periodo = periodosFiscales.find((p) => p.id === periodoId);
        return periodo ? `${periodo.periodo} - ${periodo.identificador}${periodo.vigente ? ' (Vigente)' : ''}` : '';
      },
    },
    {
      titleText: 'Estado',
      key: 'estados_globales',
      dataIndex: 'estados_globales',
      render: (e) => estadosGlobales.find((i) => i.id === e)?.descripcion,
    },
  ];

  const Formulario = () => (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
    >
      <Row gutter={16}>
        <Form.Item name="content_type_padron" hidden>
          <Input />
        </Form.Item>
        <Col xs={24} sm={24} md={8}>
          <Form.Item name="id" label="ID">
            <Input disabled />
          </Form.Item>

        </Col>

        <Col xs={24} sm={24} md={8}>
          <Form.Item
            name="descripcion"
            label="Descripción"
            hasFeedback
          >
            <Input disabled={isEditing} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Form.Item name="tipo_de_padron" label="Tipo de padrón" hasFeedback>
            <Select
              dataSource={padrones.filter((p) => p.usado_en_actualizador_de_carteras)}
              labelProp="descripcion"
              valueProp="id"
              onChange={handlePadronChange}
              disabled={isEditing}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={8}>
          <Form.Item name="periodo_fiscal" label="Periodo fiscal" hasFeedback>
            <Select dataSource={periodosFiscales} render={render} disabled />
          </Form.Item>
        </Col>

        <Col span={24}>
          <PlainTransfer
            form={form}
            dataSource={tiposDeCargos}
            label="Tipos de cargos a actualizar al grabar un padrón"
            formItemName="tipos_de_cargos"
            formItemProps={{
              rules: [{ required: true, message: 'Seleccione al menos uno' }],
            }}
            filterProp="descripcion"
          />
        </Col>
      </Row>

      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
    </Form>
  );

  return (
    <Spin tip="Cargando..." spinning={loading}>
      <br />

      {visible ? (
        <Card
          className="form-container"
          style={{ marginBottom: 16 }}
          title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Cartera`}
          extra={(
            <FormSubmitControls
              onCancel={onCancel}
              onFinish={onFinish}
              loading={loading}
            />
          )}
          bordered={false}
        >
          <Formulario />
        </Card>
      ) : (
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickDelete,
            onClickEdit,
          }}
          noControls={disabled}
          allowSort={false}
          allowSearch={false}
          rowKey="id"
          baseURI={baseURI}
          permission={{ ...permission, import: false, export: false }}
          hasAllViewPermission
          deleteText="Cancelar"
        />
      )}

      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="La Cartera"
      />
    </Spin>
  );
};

ActualizadorCarteras.propTypes = {
  callback: PropTypes.func,
  disabled: PropTypes.bool,
  permission: permissionPropTypes.isRequired,
  setForm: PropTypes.func,
  setCurrentTabKey: PropTypes.string,
};

ActualizadorCarteras.defaultProps = {
  callback: () => { },
  disabled: false,
  setCurrentTabKey: null,
  setForm: () => { },
};

export default ActualizadorCarteras;
